// Custom hook for authentication

import { useState } from 'react';
import { useAuthContext } from '../../context/AuthContext';
import errorHandler from '../utils/errorHandler';

export const useAuth = () => {
  const context = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Login with error handling
  const loginWithLoading = async (credentials) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await context.login(credentials);
      return result;
    } catch (err) {
      const handledError = errorHandler.handleError(err);
      setError(handledError.message);
      throw handledError;
    } finally {
      setLoading(false);
    }
  };

  // Register with error handling
  const registerWithLoading = async (userData) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await context.register(userData);
      return result;
    } catch (err) {
      const handledError = errorHandler.handleError(err);
      setError(handledError.message);
      throw handledError;
    } finally {
      setLoading(false);
    }
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  // Forgot password with loading handling
  const forgotPasswordWithLoading = async (email) => {
    setLoading(true);
    setError(null);
    try {
      const result = await context.forgotPassword(email);
      return result;
    } catch (err) {
      const handledError = errorHandler.handleError(err);
      setError(handledError.message);
      throw handledError;
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP with loading handling
  const verifyOtpWithLoading = async (email, otp) => {
    setLoading(true);
    setError(null);
    try {
      const result = await context.verifyOtp(email, otp);
      return result;
    } catch (err) {
      const handledError = errorHandler.handleError(err);
      setError(handledError.message);
      throw handledError;
    } finally {
      setLoading(false);
    }
  };

  // Reset password with loading handling
  const resetPasswordWithLoading = async (email, newPassword) => {
    setLoading(true);
    setError(null);
    try {
      const result = await context.resetPassword(email, newPassword);
      return result;
    } catch (err) {
      const handledError = errorHandler.handleError(err);
      setError(handledError.message);
      throw handledError;
    } finally {
      setLoading(false);
    }
  };

  return {
    // From context
    user: context.user,
    role: context.role,
    isAuthenticated: context.isAuthenticated,
    isAdmin: context.isAdmin,
    isUser: context.isUser,
    logout: context.logout,
    
    // With loading states
    login: loginWithLoading,
    register: registerWithLoading,
    forgotPassword: forgotPasswordWithLoading,
    verifyOtp: verifyOtpWithLoading,
    resetPassword: resetPasswordWithLoading,
    
    // Loading and error states
    loading: loading || context.loading,
    error,
    clearError,
  };
};

export default useAuth;
